import { Subject } from 'rxjs';
import { Component, Input, Output, EventEmitter, OnDestroy, HostBinding, Optional, Self, ElementRef, ViewChild, forwardRef } from '@angular/core';
import { ControlValueAccessor, NgControl, NG_VALUE_ACCESSOR } from '@angular/forms';
import { coerceBooleanProperty } from '@angular/cdk/coercion';
import { MatFormFieldControl as MatFormFieldControl } from '@angular/material/form-field';

export const CUSTOM_COLOR_PICKER_VALUE_ACCESSOR: any = {
  provide: NG_VALUE_ACCESSOR,
  useExisting: forwardRef(() => ColorPickerComponent),
  multi: true
};

@Component({
  selector: 'app-color-picker',
  templateUrl: './color-picker.component.html',
  styleUrls: ['./color-picker.component.scss'],
  providers: [
    { provide: MatFormFieldControl, useExisting: ColorPickerComponent }
  ],
  host: {
    '[class.example-floating]': 'shouldLabelFloat',
    '[id]': 'id',
  }
})
export class ColorPickerComponent implements OnDestroy, MatFormFieldControl<string>, ControlValueAccessor {

  static nextId = 0;

  @HostBinding() id = `color-picker-${ColorPickerComponent.nextId++}`;

  stateChanges = new Subject<void>();

  @ViewChild('colorInputField', { read: ElementRef }) inputFieldRef: ElementRef;

  errorState: boolean;
  controlType = 'color-picker';
  autofilled?: boolean;
  userAriaDescribedBy?: string;

  // Binding to realize material floating label behavior
  @HostBinding('class.floating')
  get shouldLabelFloat() {
    return this.focused || !this.empty;
  }

  /** Placeholder text for textfield. */
  private _placeholder: string;

  @Input()
  get placeholder() {
    return this._placeholder;
  }
  set placeholder(plh) {
    this._placeholder = plh;
    this.stateChanges.next();
  }

  private _required = false;

  @Input()
  get required() {
    return this._required;
  }
  set required(req) {
    this._required = coerceBooleanProperty(req);
    this.stateChanges.next();
  }

  private _disabled = false;
  @Input()
  get disabled(): boolean { return this._disabled; }
  set disabled(value: boolean) {
    this._disabled = coerceBooleanProperty(value);
    this.stateChanges.next();
  }

  private _readonly = false;
  @Input()
  get readonly(): boolean { return this._readonly; }
  set readonly(value: boolean) {
    this._readonly = coerceBooleanProperty(value);
    this.stateChanges.next();
  }

  focused = false;
  onFocusIn(event: FocusEvent) {
    if (!this.focused) {
      this.focused = true;
      this.stateChanges.next();
    }
  }
  onFocusOut(event: FocusEvent) {
    if (!this.inputFieldRef.nativeElement.contains(event.relatedTarget as Element)) {
      this.focused = false;
      this.stateChanges.next();
    }
  }

  /** The name of this form field. */
  @Input() name: string;

  /** Emits if user had selected a color in color picker component. */
  @Output() colorSelected = new EventEmitter<string>();

  /** Holds current state if color picker overlay is opened. */
  colorPickerOpen = false;

  /** The choosen color. */
  color: string;

  private onTouchedCallback: () => void = () => { };
  private onChangeCallback: (_: any) => void = () => { };

  constructor(@Optional() @Self() public ngControl: NgControl) {
    if (ngControl != null) {
      ngControl.valueAccessor = this;
    }
  }

  @Input()
  get value(): any {
    return this.color;
  }

  set value(value: any) {
    console.log('set value', value);
    if (value !== this.color) {
      this.color = value;
      this.stateChanges.next();
    }
  }

  get empty() {
    const a = this.color == null || this.color.trim().length === 0;
    // console.log('empty', a);
    return a;
  }

  setDescribedByIds(ids: string[]): void {
  }

  onContainerClick(event: MouseEvent) {
    if ((event.target as Element).tagName.toLowerCase() != 'input') {
      this.inputFieldRef.nativeElement.focus();
    }
  }

  toggleColorPicker() {
    if (this.readonly) {
      return;
    }
    this.colorPickerOpen = !this.colorPickerOpen;
  }

  closeColorPicker() {
    this.colorPickerOpen = false;
  }

  valueChanged(event: any) {
    this.colorSelected.next(this.color);
    this.onChangeCallback(this.color);
    this.stateChanges.next();
  }

  focusLost(event: any) {
    console.log('focusLost');
    if (this.colorPickerOpen) {
      this.colorPickerOpen = !this.colorPickerOpen;
    }
  }

  handleColorSelection(event: any) {
    this.color = event.color.hex;
    this.colorSelected.next(this.color);
    this.onChangeCallback(this.color);
    this.stateChanges.next();
  }

  writeValue(value: any): void {
    if (value !== this.color) {
      this.color = value;
    }
  }

  onBlur() {
    console.log('onBlur');
  }

  registerOnChange(fn: any): void {
    this.onChangeCallback = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouchedCallback = fn;
  }

  setDisabledState?(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  ngOnDestroy() {
    this.stateChanges.complete();
  }

}
